import { type BenchmarkLevel } from "@prisma/client";
import React from "react";
import { type Group, SelectInput } from "~/components/ui/core/BetterSelectInput";
import { TextInput } from "~/components/ui/core/TextInput";
import { useI18n } from "~/lib/i18n/useI18n";
import { chain, compact, flatMap, groupBy } from "~/lib/lodash";
import { arrayHasValues } from "~/lib/utils";
import { useFetchAllowedCompanyBenchmarkLevelsQuery } from "~/pages/api/benchmark-level/fetch-allowed-company-benchmark-levels";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";

type Event = {
  target: {
    name?: string;
    value: BenchmarkLevel[];
  };
};

type Props = {
  name?: string;
  value: number[];
  onChange: (event: Event) => void;
  onClose?: () => void;
  noFilter?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  dense?: boolean;
  placeholder?: string;
  includeMetaLevels?: boolean;
  excludedLevels?: EmployeeLevel[];
  useAdvancedLevels?: boolean;
  useBenchmarkLevelsInShare?: boolean;
  className?: string;
};

export const BenchmarkLevelSelector: React.FC<Props> = ({
  name,
  value: initialValue,
  onChange,
  onClose,
  multiple = false,
  disabled = false,
  dense = false,
  placeholder,
  className,
}) => {
  const { t } = useI18n();
  const formatedPlaceholder = placeholder ?? t("components.ui.level-selector.access-to-all-levels");

  const { data: allowedBenchmarkLevels } = useFetchAllowedCompanyBenchmarkLevelsQuery();

  const levelsByFramework = groupBy(allowedBenchmarkLevels, (level) => {
    return level.framework.name;
  });

  const groups: Group<BenchmarkLevel>[] = Object.values(levelsByFramework).map((levels) => {
    return {
      label: levels[0]?.framework.name ?? "Framework",
      options: levels.map((level) => {
        return level;
      }),
    };
  });

  const value = compact(
    flatMap(initialValue, (id) =>
      arrayHasValues(allowedBenchmarkLevels) ? allowedBenchmarkLevels.find((level) => level.id === id) : null
    )
  );

  return (
    <SelectInput
      dense={dense}
      disabled={disabled}
      onClose={onClose}
      formatLabel={(levels) => {
        const label = chain(levels)
          .map((level) => level.name)
          .join(", ")
          .value();

        return (
          <TextInput
            data-input
            readOnly
            className="w-full cursor-pointer select-none rounded"
            dense={dense}
            disabled={disabled}
            placeholder={
              formatedPlaceholder ??
              (multiple
                ? t("components.ui.level-selector.select-one-or-more-levels")
                : t("components.ui.level-selector.select-a-level"))
            }
            type="text"
            value={label ?? ""}
          />
        );
      }}
      formatOption={(level) => level.name}
      groups={groups}
      multiple={multiple}
      name={name}
      optionToId={(level) => level.id}
      placeholder={
        multiple
          ? t("components.ui.level-selector.select-one-or-more-levels")
          : t("components.ui.level-selector.select-a-level")
      }
      value={value}
      className={className}
      onChange={onChange}
    />
  );
};
